/**
 * Phase 18b — Options › Overrides › Lists: one card per overridden list and
 * a picker to add one. Applies to every drop this container sends to that
 * list; a slot's own amount or gateway pin wins (said once, here).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo } from 'react';

import { SearchableSelect } from '@/components/ui';
import { useCountry } from '@/hooks';

import { laneOptions } from '../../../../utils/lanes/lane-words';

import type { ContainerEditor } from '../../state/useContainerEditor';
import { ListOverrideCard } from './ListOverrideCard';
import { addOverride, patchOverride, removeOverride, setOption } from './list-override-ops';

const AUTO = 'auto';

export const ListOverridesSection: FC<{ editor: ContainerEditor; countryKey: string }> = ({ editor, countryKey }) => {
  const country = useCountry(countryKey);
  const overrides = editor.draft.listOverrides;
  const set = (next: typeof overrides) => editor.setField('listOverrides', next);
  const lists = useMemo(() => country.data?.leadsLists ?? [], [country.data]);
  const names = useMemo(() => new Map(lists.map((l) => [l.id, l.name])), [lists]);
  const routes = country.data?.routes ?? [];
  const routeName = (id: number) => routes.find((r) => r.id === id)?.name ?? null;
  // Phase 40 — a pin picks a LANE of the pool (the master's when inherited).
  const lanes = laneOptions(editor.draft.gateway.pool, routeName);
  const tlds = [
    { value: AUTO, label: 'Auto' },
    ...(country.data?.shortLinkTlds ?? []).filter((t) => t.value.toLowerCase() !== AUTO).map((t) => ({ value: t.value, label: t.displayName || t.value })),
  ];
  const addOptions = lists
    .filter((l) => !overrides.some((o) => o.wiserId === l.id))
    .map((l) => ({ value: String(l.id), label: l.name }));
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-muted-foreground">
        A list&apos;s own settings, for every drop this container sends to it. A slot&apos;s own amount or gateway still wins.
      </p>
      {overrides.map((o) => (
        <ListOverrideCard
          key={o.wiserId}
          override={o}
          listName={names.get(o.wiserId) ?? `List #${o.wiserId}`}
          lanes={lanes}
          routeName={routeName}
          tlds={tlds}
          onPatch={(patch) => set(patchOverride(overrides, o.wiserId, patch))}
          onOption={(key, value) => set(setOption(overrides, o.wiserId, key, value))}
          onRemove={() => set(removeOverride(overrides, o.wiserId))}
        />
      ))}
      <div className="w-full max-w-sm">
        <SearchableSelect
          options={addOptions}
          value=""
          onChange={(id) => set(addOverride(overrides, Number(id)))}
          placeholder="+ Override a list…"
          searchPlaceholder="Search lists…"
        />
      </div>
    </div>
  );
};
